import type { FastifySchema } from 'fastify';
import z from 'zod';

export const urlSchema = z
  .string()
  .transform(val => {
    // `new URL('https://')` (and other malformed input) throws a raw
    // TypeError, which is NOT a ZodError — it would escape `.safeParse` and
    // crash any caller that validates raw, possibly-empty input on every
    // render (e.g. the RSS add-form). Swallow it and hand the original value
    // to `.refine`, which rejects it cleanly as a ZodError instead.
    try {
      const url = new URL(val.includes('://') ? val : `https://${val}`);

      return url.href;
    } catch {
      return val;
    }
  })
  .refine(
    val => {
      try {
        const url = new URL(val);

        const isHttp = url.protocol === 'http:' || url.protocol === 'https:';

        // Basic domain validation: checks for at least one dot in the hostname
        // otherwise strings like 'kanbu' are taken as a valid URL, since JS URL structure
        // is very lenient
        const hasValidHostname = /^[\d.a-z-]+\.[a-z]{2,}$/i.test(url.hostname);

        return isHttp && hasValidHostname;
      } catch {
        return false;
      }
    },
    {
      message: 'Invalid URL',
    },
  );

export type EndpointContract = {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  schema: FastifySchema;
};

export type EndpointContractGroup = Record<
  string,
  EndpointContract | Record<string, EndpointContract>
>;
